const BOOLEAN_FEATURES = new Set<string>([
    'is_paid_source',
    'is_weekend',
    'is_business_hours',
    'is_whatsapp_validated',
    'has_demand_opened_match',
    'has_demand_phone_view',
    'has_demand_app_details_added',
    'has_demand_app_preference_times',
])

/**
 * English descriptions for lead-score attribution features (as stored in
 * lead_score_attributions), used by the office request view to explain why a
 * lead scored the way it did. Translated from the supply-facing signal texts.
 *
 * Some features are value-specific (request_type, is_paid_source); the rest
 * depend on the name only. Features without a description return null and are
 * shown with just their name and value. Unlike the pro dashboard, the office
 * also describes the deliberately excluded ("Bewust uitgesloten") features,
 * since it shows both positive and negative impacts.
 */
const FEATURE_DESCRIPTION_BY_NAME: Record<string, string> = {
    // Creation signals
    opt_open_answered_pct: 'Answered open questions',
    opt_after_conv_answered_pct: 'Added extra details after submitting',
    opt_answered_pct: 'Answered many optional and multiple-choice questions',
    opt_mc_answered_pct: 'Answered optional multiple-choice questions',
    nr_matches: 'Actively compares multiple companies',
    nr_first_choice_matches: 'Started the request from a company profile',
    details_length: 'Wrote a clear description',
    in_wizard_s: 'Took time to fill in the request',
    before_wizard_s: 'Spent time on the website before starting the request',
    is_business_hours: 'Submitted the request during business hours',
    is_weekend: 'Submitted the request during the weekend',
    nr_previous_requests_by_user: 'Has used Trustoo before',

    // Engagement signals
    has_demand_opened_match: 'Viewed a match with a company',
    nr_demand_opened_matches: 'Viewed multiple matches with companies',
    has_demand_phone_view: 'Viewed a phone number to call',
    nr_demand_messages: 'Actively sends messages to companies',
    pct_emails_to_demand_opened: "Opens Trustoo's emails",
    pct_emails_to_demand_clicked: "Clicks links in Trustoo's emails",
    ga_count_demand_dashboard_visit: 'Keeps returning to the request overview',
    ga_count_click_checklist_demand_dashboard: 'Uses the checklist to take the next step',
    is_whatsapp_validated: 'Is directly reachable via a verified WhatsApp number',

    // Additional signals
    time_until_first_demand_enrichment_h: 'Responded quickly with additional information',
    ga_count_open_topic_checklist_demand_dashboard: 'Explored the next steps in detail',
    ga_count_menu_inbox_demand_dashboard: 'Actively checked their inbox for messages',
    pct_whatsapp_to_demand_delivered: 'Is reachable via WhatsApp',
    ga_count_dd_phone_modal_contact: 'Sought phone contact with a company',
    ga_count_menu_myrequests_demand_dashboard: 'Actively views their requests',
    ga_count_submit_autoquote_getintouch: 'Asked for contact after a price proposal',
    ga_count_click_reviews_demand_dashboard: 'Read reviews of companies',
    ga_count_click_messages_demand_dashboard: 'Actively checked their message box',
    ga_count_dd_phone_modal_no_contact_call_back: 'Asked to be called back',
    ga_count_click_quick_reply_demand: 'Responded quickly to messages from companies',
    pct_whatsapp_to_demand_read: 'Reads WhatsApp messages from companies',
    ga_count_submit_add_answers_dd: 'Filled in additional questions',
    ga_count_click_message_checklist_demand_dashboard: 'Made contact via the checklist',
    ga_count_click_message_reviews_demand_dashboard: 'Made contact after reading reviews',
    ga_count_submit_add_photos_dd: 'Added photos to the request',
    ga_count_submit_add_details_dd: 'Added extra details to the request',
    ga_count_click_call_reviews_demand_dashboard: 'Called a company after viewing reviews',
    ga_count_send_message_demand_dashboard: 'Sent a message to a company',
    ga_count_click_view_profile_demand_dashboard: "Viewed a company's profile",
    ga_count_submit_autoquote_availability: 'Asked about availability after a price proposal',
    nr_files: 'Added files to the request',
    ga_count_add_multiple_match_dashboard: 'Added multiple companies to the comparison',
    ga_count_click_message_profile_demand_dashboard: 'Made contact after viewing a company profile',
    ga_count_autoquote_thankyoupage_whatsapp: 'Chose WhatsApp contact after a price proposal',
    ga_count_autoquote_thankyoupage_message: 'Chose message contact after a price proposal',
    ga_count_open_compare_match_dd: 'Compared companies side by side',

    // Deliberately excluded signals (negative / non-positive)
    pct_emails_to_demand_bounced: 'Bounce is a negative reachability signal',
    pct_whatsapp_to_demand_failed_or_rejected:
        'Failed/rejected WhatsApp is a negative reachability signal',
    ga_count_dd_phone_modal_no_contact: 'Opened the phone modal but did not make contact',
}

export function getLeadScoreFeatureDescription(
    featureName: string,
    featureValue: string,
): string | null {
    if (featureName === 'request_type') {
        if (featureValue === 'availability') {
            return 'Wants to know if you are available'
        }

        if (featureValue === 'contact') {
            return 'Filled in the contact form'
        }

        return null
    }

    if (featureName === 'is_paid_source') {
        return featureValue === '0'
            ? 'Found Trustoo on their own initiative'
            : 'Came in via a targeted search'
    }

    return FEATURE_DESCRIPTION_BY_NAME[featureName] ?? null
}

function isBooleanFeature(featureName: string): boolean {
    return BOOLEAN_FEATURES.has(featureName)
}

function isPercentageFeature(featureName: string): boolean {
    return /^pct_/.test(featureName) || /_pct$/.test(featureName)
}

/**
 * Renders a raw attribution feature_value for display: booleans as
 * "true"/"false" and percentages (stored as 0.0-1.0 fractions) on a 0-100
 * scale. Any other value is returned unchanged.
 */
export function formatLeadScoreFeatureValue(featureName: string, featureValue: string): string {
    if (isBooleanFeature(featureName)) {
        return featureValue === '1' ? 'true' : 'false'
    }

    if (isPercentageFeature(featureName)) {
        const fraction = Number(featureValue)

        if (Number.isFinite(fraction)) {
            return `${featureName === 'after_conversion_quality_pct' ? fraction : Math.round(fraction * 100)}%`
        }
    }

    return featureValue
}
